import { Link } from "react-router-dom";

/**
 * PageBanner — reusable royal-blue hero strip for inner pages.
 * Renders a bold, large page title with gold accent + optional subtitle and
 * breadcrumb, so every inner page (About, Products, Contact, etc.) opens
 * with a strong visual banner instead of a plain H1.
 *
 * Usage:
 *   <PageBanner label="About Us" title="Better Choices. Meaningful Opportunities." subtitle="…" />
 */
export default function PageBanner({ label, title, subtitle, crumbs, tone = "royal" }) {
  const bg =
    tone === "gold"
      ? "from-gold-deep via-gold to-gold-deep"
      : tone === "plum"
      ? "from-plum via-royal-deep to-plum"
      : "from-royal-deep via-royal to-navy";
  return (
    <section
      data-testid="page-banner"
      className={`relative bg-royal overflow-hidden`}
    >
      <img
        src="/product-images/home-banner.jpg"
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-10"
      />
      <div className={`absolute inset-0 bg-gradient-to-br ${bg}`} />
      {/* Diagonal texture */}
      <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay [background-image:repeating-linear-gradient(45deg,#fff_0_1px,transparent_1px_12px)]" />
      {/* Gold halo */}
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-gold/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-gold/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        {crumbs && (
          <nav className="text-[11px] uppercase tracking-widest text-white/60 mb-4">
            {crumbs.map((c, i) => (
              <span key={c.label}>
                {c.to ? (
                  <Link to={c.to} className="hover:text-gold transition-colors">
                    {c.label}
                  </Link>
                ) : (
                  <span className="text-gold/90">{c.label}</span>
                )}
                {i < crumbs.length - 1 && <span className="mx-2 text-white/30">/</span>}
              </span>
            ))}
          </nav>
        )}

        {label && (
          <div className="flex items-center gap-3">
            <span className="h-[2px] w-10 bg-gold" />
            <div className="text-[11px] uppercase tracking-[0.4em] text-gold font-semibold">
              {label}
            </div>
          </div>
        )}

        <h1
          data-testid="page-banner-title"
          className="mt-4 font-serif font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white max-w-4xl tracking-tight"
        >
          {title}
        </h1>

        {subtitle && (
          <p className="mt-5 text-white/85 max-w-2xl leading-relaxed text-base sm:text-lg">
            {subtitle}
          </p>
        )}
      </div>

      {/* Bottom gold hairline */}
      <div className="relative h-[3px] bg-gradient-to-r from-transparent via-gold to-transparent" />
    </section>
  );
}
